import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useIsLargeScreen } from '../../hooks/useIsLargeScreen';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Bell, CheckCircle2, AlertTriangle, MessageSquare, Send,
  ShieldCheck, Calendar, Banknote, Check, Loader2, Trash2, X,
  UserX, Ban, Star
} from 'lucide-react';
import { toast } from 'sonner';

const typeIcons = {
  new_application: Send,
  application_submitted: Send,
  application_accepted: CheckCircle2,
  application_rejected: AlertTriangle,
  urgent_till_alert: Banknote,
  till_discrepancy: Banknote,
  chat_message: MessageSquare,
  smart_id_confirmed: ShieldCheck,
  shift_cancelled: Ban,
  worker_cancelled: Ban,
  penalty: AlertTriangle,
  no_show: UserX,
  rating_received: Star,
  shift_completed: CheckCircle2,
  info: Bell,
};

const typeColors = {
  new_application: 'bg-blue-100 text-blue-700',
  application_submitted: 'bg-blue-100 text-blue-700',
  application_accepted: 'bg-emerald-100 text-emerald-700',
  application_rejected: 'bg-red-100 text-red-700',
  urgent_till_alert: 'bg-amber-100 text-amber-700',
  till_discrepancy: 'bg-amber-100 text-amber-700',
  chat_message: 'bg-purple-100 text-purple-700',
  smart_id_confirmed: 'bg-teal-100 text-teal-700',
  shift_cancelled: 'bg-gray-100 text-gray-700',
  worker_cancelled: 'bg-orange-100 text-orange-700',
  penalty: 'bg-red-100 text-red-700',
  no_show: 'bg-red-100 text-red-700',
  rating_received: 'bg-amber-100 text-amber-700',
  shift_completed: 'bg-emerald-100 text-emerald-700',
  info: 'bg-slate-100 text-slate-700',
};

const getActionUrl = (notification, role) => {
  if (notification.actionUrl) return notification.actionUrl;
  const prefix = role === 'manager' ? '/manager' : '/worker';
  switch (notification.type) {
    case 'new_application':
      return `${prefix}/applicants`;
    case 'application_accepted':
    case 'application_rejected':
    case 'application_submitted':
    case 'shift_cancelled':
    case 'worker_cancelled':
      return `${prefix}/my-shifts`;
    case 'chat_message':
      return `${prefix}/chat`;
    case 'urgent_till_alert':
    case 'till_discrepancy':
      return role === 'manager' ? '/manager/alerts' : '/worker/till-checks';
    case 'penalty':
    case 'no_show':
      return `${prefix}/my-shifts`;
    case 'shift_completed':
      return `${prefix}/my-shifts?tab=history`;
    case 'smart_id_confirmed':
      return `${prefix}/profile`;
    case 'rating_received':
      return `${prefix}/profile`;
    default:
      return null;
  }
};

export default function NotificationsPage() {
  const { api, user } = useAuth();
  const isLargeScreen = useIsLargeScreen();
  const walkthroughId = !isLargeScreen
    ? (user?.role === 'manager' ? 'wt-mgr-notifications' : 'wt-worker-notifications')
    : undefined;
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [markingAll, setMarkingAll] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
    } catch {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    } catch {}
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await api.patch('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Failed to mark all as read');
    } finally {
      setMarkingAll(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    setDeletingId(id);
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      toast.success('Notification deleted');
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const handleClick = (notification) => {
    if (!notification.isRead) handleMarkRead(notification.id);
    const url = getActionUrl(notification, user?.role);
    if (url) navigate(url);
  };

  const filtered = filter === 'unread'
    ? notifications.filter(n => !n.isRead)
    : notifications;

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-10 w-48" />
        {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20" />)}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']" data-walkthrough={walkthroughId}>Notifications</h1>
          <p className="text-slate-500 mt-1">{unreadCount} unread</p>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline" onClick={handleMarkAllRead} disabled={markingAll}
            className="rounded-full text-sm h-9" data-testid="mark-all-read-btn"
          >
            {markingAll ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
            Mark all read
          </Button>
        )}
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-slate-100 p-1 rounded-xl h-auto">
          <TabsTrigger value="all" className="rounded-lg min-h-[44px] px-5">All</TabsTrigger>
          <TabsTrigger value="unread" className="rounded-lg min-h-[44px] px-5">
            Unread {unreadCount > 0 && `(${unreadCount})`}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {filtered.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Bell className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              {filter === 'unread' ? 'No Unread Notifications' : 'No Notifications'}
            </h3>
            <p className="text-slate-500">
              {filter === 'unread' ? "You're all caught up!" : 'Notifications will appear here.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(n => {
            const Icon = typeIcons[n.type] || Bell;
            const colorClass = typeColors[n.type] || typeColors.info;
            return (
              <Card
                key={n.id}
                className={`border-0 shadow-sm cursor-pointer transition-all hover:shadow-md ${!n.isRead ? 'bg-blue-50/50 border-l-4 border-l-blue-500' : ''}`}
                onClick={() => handleClick(n)}
                data-testid={`notification-${n.id}`}
              >
                <CardContent className="p-4 flex items-start gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${colorClass}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className={`text-sm font-semibold truncate ${!n.isRead ? 'text-slate-900' : 'text-slate-600'}`}>
                        {n.title}
                      </h4>
                      <span className="text-xs text-slate-400 shrink-0">{formatTime(n.createdAt)}</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{n.body}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!n.isRead && <div className="h-2.5 w-2.5 bg-blue-500 rounded-full mt-1.5" />}
                    <Button
                      variant="ghost" size="sm"
                      className="h-8 w-8 p-0 text-slate-400 hover:text-red-500 hover:bg-red-50"
                      onClick={(e) => handleDelete(n.id, e)}
                      disabled={deletingId === n.id}
                      data-testid={`delete-notification-${n.id}`}
                    >
                      {deletingId === n.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
